import {useCallback, useMemo, useRef, useState} from 'react';
import {TRANSITION_DIRECTION} from '../../../constant/Application';
import {FORM_STEP_ORDER} from '../../../constant/FormStep';

const [firstStep] = FORM_STEP_ORDER;
const noop = () => null;

function useForm() {
  const [step, setStep] = useState(firstStep);
  const [lastCompletedStep, setLastCompletedStep] = useState(firstStep);
  const [transitionDirection, setTransitionDirection] = useState(TRANSITION_DIRECTION.FORWARD);
  const [formValues, setFormValues] = useState({});
  /*
    The mounted FormStep is the only thing that knows whether the step currently on
    screen is actually valid, and its handleOnGoNext is the only path that saves a
    step's values into formValues and reports it to analytics. The breadcrumb
    back/forward control below lives outside FormStepContext (it is rendered once,
    alongside the step transitions, not inside one), so without this it had its own
    unvalidated route straight to goToNextStep -- R11: it could walk past a required
    field with nothing filled in, silently dropping that step's values instead of
    ever writing them to formValues.
  */
  const activeStepApiRef = useRef({isStepValid: true, handleOnGoNext: noop});
  const [isActiveStepValid, setIsActiveStepValid] = useState(true);

  const registerStepApi = useCallback(({handleOnGoNext, isStepValid}) => {
    activeStepApiRef.current = {handleOnGoNext, isStepValid};
    setIsActiveStepValid(isStepValid);
  }, []);

  const nextButtonProps = useMemo(() => {
    const isFirstStep = step === firstStep;

    return {
      text    : isFirstStep ? 'Start' : 'OK',
      hasIcon : !isFirstStep
    };
  }, [step]);

  const updateValues = useCallback(newValues => {
    setFormValues(prevState => {
      const nextState = {
        ...prevState,
        ...newValues
      };

      return nextState;
    });
  }, []);

  const goToNextStep = useCallback(() => {
    const currentStepIndex = FORM_STEP_ORDER.indexOf(step);
    const lastCompletedStepIndex = FORM_STEP_ORDER.indexOf(lastCompletedStep);

    if (currentStepIndex < FORM_STEP_ORDER.length - 1) {
      const nextStep = FORM_STEP_ORDER[currentStepIndex + 1];

      setTransitionDirection(TRANSITION_DIRECTION.FORWARD);

      if (currentStepIndex + 1 > lastCompletedStepIndex) {
        setLastCompletedStep(nextStep);
      }

      setStep(nextStep);
    }
  }, [lastCompletedStep, step]);

  const goToPreviousStep = useCallback(() => {
    const currentStepIndex = FORM_STEP_ORDER.indexOf(step);

    if (currentStepIndex > 1) {
      const nextStep = FORM_STEP_ORDER[currentStepIndex - 1];

      setTransitionDirection(TRANSITION_DIRECTION.BACKWARD);

      setStep(nextStep);
    }
  }, [step]);

  /*
    The breadcrumb's own advance: it must leave the active step exactly the way its
    own OK button would, so a step revisited from the breadcrumb can't skip validation
    or get its edits dropped just because a different control triggered the move.
  */
  const advanceActiveStep = useCallback(() => {
    activeStepApiRef.current.handleOnGoNext();
  }, []);

  const stepIndex = FORM_STEP_ORDER.indexOf(step);
  const totalNumberOfSteps = FORM_STEP_ORDER.length - 1;
  const lastCompletedStepIndex = FORM_STEP_ORDER.indexOf(lastCompletedStep);

  const formContext = {
    advanceActiveStep,
    isActiveStepValid,
    lastCompletedStep,
    lastCompletedStepIndex,
    stepIndex,
    totalNumberOfSteps,
    formValues,
    goToNextStep,
    goToPreviousStep,
    nextButtonProps,
    onSkipStep : goToNextStep,
    registerStepApi,
    step,
    updateValues
  };

  return {
    formContext,
    stepIndex,
    transitionDirection
  };
}

export default useForm;
